import { Button, MenuItem, setRef } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';

export interface SelectOption<T> {
  value: T;
  label: string;
  disabled?: boolean;
  group?: string;
}

function matchesOption<T>(
  query: string,
  option: SelectOption<T>,
  _index: number | undefined,
  exact = false,
) {
  const text =
    `${option.label} ${option.value ?? ''} ${option.group ?? ''}`.toLocaleLowerCase();
  const search = query.trim().toLocaleLowerCase();
  return exact ? option.label.toLocaleLowerCase() === search : text.includes(search);
}

/** Shared presentation only: callers own values, validation, and selection effects. */
export function SelectField<T extends string | number | null | undefined>({
  id,
  label,
  value,
  options,
  onChange,
  filterable = false,
  fill = false,
  disabled = false,
  title,
}: {
  id?: string;
  label: string;
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  filterable?: boolean;
  fill?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  const selected = options.find((option) => option.value === value);
  const selectedLabel = selected?.label ?? 'Choose an option';
  const [active, setActive] = useState<SelectOption<T> | null>(selected ?? null);
  const [menuId, setMenuId] = useState<string>();
  const trigger = useRef<HTMLButtonElement | null>(null);
  const spaceSelectPending = useRef(false);
  const chooseOption = (option: SelectOption<T>) => {
    if (!option.disabled && option.value !== value) onChange(option.value);
  };
  const dismissFixedMenu = (event: KeyboardEvent<HTMLElement>) => {
    // Focus stays outside the portal. Route dismissal through its overlay so
    // it closes this menu before an enclosing dialog sees the key.
    event.preventDefault();
    event.stopPropagation();
    if (menuId)
      trigger.current?.ownerDocument
        .getElementById(menuId)
        ?.dispatchEvent(
          new window.KeyboardEvent('keydown', { key: 'Escape', bubbles: true }),
        );
  };
  const focusCombobox = () =>
    trigger.current?.closest<HTMLElement>('[role="combobox"]')?.focus();
  useLayoutEffect(() => {
    const target = trigger.current?.parentElement;
    // Blueprint Select overwrites popoverTargetProps.tabIndex with undefined.
    // Bridge that vendor limitation without adding another interactive wrapper.
    if (!target) return;
    if (!filterable) {
      target.tabIndex = disabled ? -1 : 0;
      return;
    }
    target.removeAttribute('tabindex');
    // Blueprint also writes aria-expanded after target props, even when its
    // searchable wrapper has no role. Move that state to the actual button;
    // the popup's input remains the only combobox in searchable mode.
    const button = trigger.current!;
    let expanded = target.getAttribute('aria-expanded') ?? 'false';
    const syncExpanded = () => {
      const next = target.getAttribute('aria-expanded');
      // Our removal queues one more notification; it must not write again.
      if (next === null) return;
      expanded = next;
      button.setAttribute('aria-expanded', next);
      target.removeAttribute('aria-expanded');
    };
    syncExpanded();
    const observer = new MutationObserver(syncExpanded);
    observer.observe(target, { attributes: true, attributeFilter: ['aria-expanded'] });
    return () => {
      observer.disconnect();
      // Restore vendor-owned state for StrictMode setup or a mode change.
      // A synchronous mode change can precede a queued vendor notification.
      // Keep a newer React write instead of restoring our last observed value.
      if (!target.hasAttribute('aria-expanded'))
        target.setAttribute('aria-expanded', expanded);
      button.removeAttribute('aria-expanded');
    };
  }, [disabled, filterable]);
  const typeahead = useRef({ text: '', time: 0 });
  const handleTypeahead = (event: KeyboardEvent<HTMLElement>) => {
    // Blueprint handles arrows/Enter. Fixed menus also support native-style typeahead.
    if (
      filterable ||
      disabled ||
      event.key.length !== 1 ||
      event.key === ' ' ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.nativeEvent.isComposing
    )
      return;
    const now = Date.now();
    const previous = now - typeahead.current.time < 700 ? typeahead.current.text : '';
    const text = previous + event.key.toLocaleLowerCase();
    typeahead.current = { text, time: now };
    const enabled = options.filter((option) => !option.disabled);
    const start = enabled.findIndex((option) => option.value === value) + 1;
    const ordered = [...enabled.slice(start), ...enabled.slice(0, start)];
    const match =
      ordered.find((option) => option.label.toLocaleLowerCase().startsWith(text)) ??
      ordered.find((option) =>
        option.label.toLocaleLowerCase().startsWith(event.key.toLocaleLowerCase()),
      );
    if (match) {
      setActive(match);
      if (match.value !== value) onChange(match.value);
    }
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Select<SelectOption<T>>
      items={options}
      itemsEqual="value"
      activeItem={
        options.find((option) => active !== null && option.value === active.value) ?? null
      }
      onActiveItemChange={setActive}
      itemDisabled={(option) => Boolean(option.disabled)}
      filterable={filterable}
      fill={fill}
      disabled={disabled}
      resetOnClose
      resetOnSelect
      resetOnQuery={false}
      itemPredicate={matchesOption}
      itemRenderer={(option, { handleClick, handleFocus, modifiers, id, ref }) => (
        <MenuItem
          key={`${typeof option.value}:${option.value}`}
          ref={(element) => {
            // MenuItem places its id prop on the inner anchor, but the option role
            // lives on this outer li. Active-descendant must identify that option.
            if (element) {
              if (id) element.id = id;
              // Exploring a row changes accessibility selection; the checkmark
              // and caller value remain committed until Enter/click.
              element.setAttribute('aria-selected', String(modifiers.active));
            }
            setRef(ref, element);
          }}
          text={option.label}
          label={option.group}
          active={modifiers.active}
          disabled={modifiers.disabled}
          roleStructure="listoption"
          tabIndex={-1}
          selected={option.value === value}
          onClick={handleClick}
          onFocus={handleFocus}
          multiline
        />
      )}
      onItemSelect={chooseOption}
      noResults={
        <MenuItem disabled roleStructure="listoption" text="No matching options" />
      }
      inputProps={{
        'aria-label': `Filter ${label}`,
        // QueryList selects on keyup; Enter must not submit an enclosing form.
        onKeyDown: (event) => {
          if (event.key === 'Enter') event.preventDefault();
          if (event.key !== 'Escape' && event.key !== 'Tab')
            event.nativeEvent.stopPropagation();
        },
      }}
      popoverTargetProps={{
        'aria-label': filterable ? undefined : `${label}: ${selectedLabel}`,
        'aria-activedescendant':
          !filterable && menuId && active
            ? `${menuId}-item-${options.findIndex((option) => option.value === active.value)}`
            : undefined,
        // Blueprint otherwise references its lazy, unmounted listbox while closed.
        'aria-controls': menuId,
        onKeyDown: (event) => {
          if (
            !filterable &&
            menuId &&
            event.key === 'Escape' &&
            event.currentTarget.getAttribute('aria-expanded') === 'true'
          ) {
            dismissFixedMenu(event);
            return;
          }
          if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation();
          if (!filterable && event.key === ' ') {
            event.preventDefault();
            // First Space opens; a later Space commits on keyup. Repeats must
            // not turn the opening keystroke into an accidental selection.
            if (!event.repeat)
              spaceSelectPending.current =
                event.currentTarget.getAttribute('aria-expanded') === 'true';
          }
          if (!filterable && event.key === 'Enter') event.preventDefault();
          handleTypeahead(event);
        },
        onKeyUp: (event) => {
          if (!filterable && event.key === ' ') {
            const shouldSelect = spaceSelectPending.current;
            spaceSelectPending.current = false;
            if (
              shouldSelect &&
              active &&
              event.currentTarget.getAttribute('aria-expanded') === 'true'
            ) {
              chooseOption(active);
              dismissFixedMenu(event);
            }
          }
          if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation();
        },
      }}
      popoverProps={{
        minimal: true,
        autoFocus: filterable,
        placement: 'bottom-start',
        matchTargetWidth: fill,
        // Portals avoid clipping in scrollable dialogs/pickers; Blueprint's overlay
        // stack keeps focus and Escape scoped to the innermost open popover.
        usePortal: true,
        captureDismiss: true,
        onOpening: (node) => {
          setActive(selected ?? null);
          setMenuId(node.querySelector('[role="listbox"]')?.id);
        },
        onOpened: () => {
          // Select-only comboboxes retain DOM focus on their trigger; Blueprint
          // forwards its arrow/Enter handlers and active-descendant identifies the row.
          if (!filterable) focusCombobox();
        },
        onClosed: () => {
          spaceSelectPending.current = false;
          setMenuId(undefined);
        },
      }}
      popoverContentProps={{
        style: { maxWidth: 'calc(100vw - 24px)', overflowWrap: 'anywhere' },
      }}
      menuProps={{
        'aria-label': `${label} options`,
        tabIndex: -1,
        onKeyDown: (event) => {
          if (event.key !== 'Escape' && event.key !== 'Tab')
            event.nativeEvent.stopPropagation();
          handleTypeahead(event);
        },
        onKeyUp: (event) => {
          if (event.key !== 'Escape' && event.key !== 'Tab')
            event.nativeEvent.stopPropagation();
        },
        style: { maxHeight: 'min(300px, 40dvh)', overflowY: 'auto' },
      }}
    >
      <Button
        id={id}
        ref={trigger}
        type="button"
        tabIndex={filterable ? undefined : -1}
        onFocus={filterable ? undefined : focusCombobox}
        onMouseDown={
          filterable
            ? undefined
            : (event) => {
                event.preventDefault();
                focusCombobox();
              }
        }
        aria-label={`${label}: ${selectedLabel}`}
        aria-haspopup={filterable ? 'listbox' : undefined}
        aria-controls={filterable ? menuId : undefined}
        title={title}
        disabled={disabled}
        fill={fill}
        alignText="start"
        endIcon="double-caret-vertical"
        style={{ maxWidth: '100%' }}
        text={selectedLabel}
      />
    </Select>
  );
}
